import { useState, useEffect } from 'react'
import {
  X,
  Play,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import type {
  ToolCatalogItem,
  ToolParameter,
  ToolExecResponse,
  DangerLevel,
} from '@/types/plugins'

interface ToolTestingPanelProps {
  tool: ToolCatalogItem
  onClose: () => void
  onExecute: (tool: ToolCatalogItem, args: Record<string, unknown>) => Promise<ToolExecResponse>
}

const dangerWarnings: Record<DangerLevel, string | null> = {
  SAFE: null,
  LOW: null,
  MEDIUM: 'This tool may make changes to your system.',
  HIGH: 'This tool can make significant changes. Use with caution.',
  CRITICAL: 'WARNING: This tool can make destructive changes that may be irreversible!',
}

function ParameterInput({
  param,
  value,
  onChange,
}: {
  param: ToolParameter
  value: unknown
  onChange: (value: unknown) => void
}) {
  const inputId = `param-${param.name}`

  // Handle different parameter types
  if (param.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          id={inputId}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-surface-border bg-background text-accent focus:ring-accent focus:ring-offset-0"
        />
        <span className="text-sm text-text">{param.name}</span>
        {param.required && <span className="text-red-400 text-xs">*</span>}
      </label>
    )
  }

  if (param.enum && param.enum.length > 0) {
    return (
      <div>
        <label htmlFor={inputId} className="block text-sm text-text mb-1">
          {param.name}
          {param.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        <select
          id={inputId}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 bg-background border border-surface-border rounded-lg text-text text-sm focus:outline-none focus:border-accent/50"
        >
          <option value="">Select {param.name}...</option>
          {param.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {param.description && (
          <p className="text-xs text-text-muted mt-1">{param.description}</p>
        )}
      </div>
    )
  }

  if (param.type === 'number' || param.type === 'integer') {
    return (
      <div>
        <label htmlFor={inputId} className="block text-sm text-text mb-1">
          {param.name}
          {param.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        <input
          type="number"
          id={inputId}
          value={value !== undefined ? String(value) : ''}
          onChange={(e) =>
            onChange(e.target.value ? Number(e.target.value) : undefined)
          }
          min={param.minimum}
          max={param.maximum}
          placeholder={
            param.default !== undefined ? `Default: ${param.default}` : ''
          }
          className="w-full px-3 py-2 bg-background border border-surface-border rounded-lg text-text text-sm focus:outline-none focus:border-accent/50 font-mono"
        />
        {param.description && (
          <p className="text-xs text-text-muted mt-1">{param.description}</p>
        )}
      </div>
    )
  }

  if (param.type === 'object' || param.type === 'array') {
    const jsonValue =
      typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)
    return (
      <div>
        <label htmlFor={inputId} className="block text-sm text-text mb-1">
          {param.name} (JSON)
          {param.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        <textarea
          id={inputId}
          value={jsonValue}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value))
            } catch {
              onChange(e.target.value) // Keep as string if invalid JSON
            }
          }}
          rows={4}
          placeholder={`Enter ${param.type} as JSON...`}
          className="w-full px-3 py-2 bg-background border border-surface-border rounded-lg text-text text-sm focus:outline-none focus:border-accent/50 font-mono resize-y"
        />
        {param.description && (
          <p className="text-xs text-text-muted mt-1">{param.description}</p>
        )}
      </div>
    )
  }

  // Default: string input
  return (
    <div>
      <label htmlFor={inputId} className="block text-sm text-text mb-1">
        {param.name}
        {param.required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type="text"
        id={inputId}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder={
          param.default !== undefined
            ? `Default: ${param.default}`
            : param.description || ''
        }
        className="w-full px-3 py-2 bg-background border border-surface-border rounded-lg text-text text-sm focus:outline-none focus:border-accent/50"
      />
      {param.description && param.description !== value && (
        <p className="text-xs text-text-muted mt-1">{param.description}</p>
      )}
    </div>
  )
}

function ResultDisplay({ result }: { result: ToolExecResponse }) {
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(true)

  const copyResult = () => {
    navigator.clipboard.writeText(JSON.stringify(result, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="border border-surface-border rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
          result.ok ? 'bg-emerald-500/10' : 'bg-red-500/10'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-text-muted" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-muted" />
          )}
          <span
            className={`text-sm font-medium ${
              result.ok ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {result.ok ? 'Success' : 'Error'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {result.durationMs}ms
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation()
              copyResult()
            }}
            className="p-1 hover:bg-surface-hover rounded"
            title="Copy result"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-text-muted" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-3 bg-background/50">
          {result.error ? (
            <div className="text-sm text-red-400">{result.error}</div>
          ) : (
            <pre className="text-xs text-text font-mono whitespace-pre-wrap overflow-x-auto">
              {JSON.stringify(result.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

export default function ToolTestingPanel({
  tool,
  onClose,
  onExecute,
}: ToolTestingPanelProps) {
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [isExecuting, setIsExecuting] = useState(false)
  const [result, setResult] = useState<ToolExecResponse | null>(null)

  const warning = dangerWarnings[tool.dangerLevel]

  // Initialize form values with defaults
  useEffect(() => {
    const defaults: Record<string, unknown> = {}
    tool.parameters.forEach((param) => {
      if (param.default !== undefined) {
        defaults[param.name] = param.default
      }
    })
    setFormValues(defaults)
  }, [tool])

  const handleExecute = async () => {
    setIsExecuting(true)
    setResult(null)
    try {
      // Filter out undefined values
      const args = Object.fromEntries(
        Object.entries(formValues).filter(([, v]) => v !== undefined && v !== '')
      )
      const response = await onExecute(tool, args)
      setResult(response)
    } catch (err) {
      setResult({
        ok: false,
        error: err instanceof Error ? err.message : 'Execution failed',
        durationMs: 0,
      })
    } finally {
      setIsExecuting(false)
    }
  }

  const setParamValue = (name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const requiredParams = tool.parameters.filter((p) => p.required)
  const optionalParams = tool.parameters.filter((p) => !p.required)

  const canExecute = requiredParams.every(
    (p) => formValues[p.name] !== undefined && formValues[p.name] !== ''
  )

  return (
    <div className="fixed inset-x-0 bottom-0 bg-surface border-t border-surface-border shadow-2xl z-50 animate-in slide-in-from-bottom duration-300">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-border">
          <div>
            <h3 className="text-lg font-semibold text-text">{tool.name}</h3>
            <p className="text-sm text-text-muted">{tool.description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        {/* Warning */}
        {warning && (
          <div className="mx-4 mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-yellow-400">{warning}</span>
          </div>
        )}

        {/* Content */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[50vh] overflow-y-auto">
          {/* Parameters */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-text">Parameters</h4>

            {tool.parameters.length === 0 ? (
              <p className="text-sm text-text-muted">
                This tool takes no parameters.
              </p>
            ) : (
              <>
                {/* Required Parameters */}
                {requiredParams.length > 0 && (
                  <div className="space-y-3">
                    {requiredParams.map((param) => (
                      <ParameterInput
                        key={param.name}
                        param={param}
                        value={formValues[param.name]}
                        onChange={(v) => setParamValue(param.name, v)}
                      />
                    ))}
                  </div>
                )}

                {/* Optional Parameters */}
                {optionalParams.length > 0 && (
                  <details className="group">
                    <summary className="text-xs text-text-muted cursor-pointer hover:text-text flex items-center gap-1">
                      <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
                      Optional parameters ({optionalParams.length})
                    </summary>
                    <div className="space-y-3 mt-3 pl-4 border-l border-surface-border">
                      {optionalParams.map((param) => (
                        <ParameterInput
                          key={param.name}
                          param={param}
                          value={formValues[param.name]}
                          onChange={(v) => setParamValue(param.name, v)}
                        />
                      ))}
                    </div>
                  </details>
                )}
              </>
            )}

            {/* Execute Button */}
            <button
              onClick={handleExecute}
              disabled={!canExecute || isExecuting}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                canExecute && !isExecuting
                  ? 'bg-accent text-white hover:bg-accent-hover'
                  : 'bg-surface-hover text-text-muted cursor-not-allowed'
              }`}
            >
              {isExecuting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Execute Tool
                </>
              )}
            </button>
          </div>

          {/* Result */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-text">Result</h4>

            {result ? (
              <ResultDisplay result={result} />
            ) : (
              <div className="h-40 flex items-center justify-center border border-dashed border-surface-border rounded-lg">
                <p className="text-sm text-text-muted">
                  Execute the tool to see results here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
